import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Users, Link2, ImageOff, Star, KeyRound, RefreshCw } from 'lucide-react'
import { MatchBadge, ReviewBadge } from '../components/ExternalBadges'
import { CatalogSearch } from '../components/CatalogSearch'
import { useExternalArtists, useProviders, useSyncArtists } from '../hooks/useExternalCatalog'
import type { ArtistListParams, ExternalArtist } from '../api/externalCatalogApi'

// Each view is one question a manager comes here to answer, not a raw column filter.
type View = 'all' | 'ours' | 'suggested' | 'new' | 'shortlisted'

const VIEWS: Array<{ value: View; label: string; params: ArtistListParams }> = [
  { value: 'all', label: 'All', params: {} },
  { value: 'ours', label: 'Already ours', params: { matchStatus: 'linked' } },
  { value: 'suggested', label: 'Possible matches', params: { matchStatus: 'suggested' } },
  { value: 'new', label: 'Not in ARTHAUS', params: { matchStatus: 'unmatched' } },
  { value: 'shortlisted', label: 'Shortlisted', params: { reviewStatus: 'shortlisted' } },
]

const PAGE_SIZE = 50

/** "12 of 40 held" once artworks are synced; an em-dash before, so 0 never means "unknown". */
function CoverageCell({ artist }: { artist: ExternalArtist }) {
  if (artist.matchStatus !== 'linked') return <span className="text-slate-400">—</span>
  if (!artist.artworksSyncedAt && !artist.mirrored) {
    return <span className="text-xs text-slate-500">Not synced</span>
  }
  const missing = (artist.mirrored ?? 0) - (artist.have ?? 0)
  return (
    <span className="text-sm tabular-nums">
      {artist.have ?? 0} / {artist.mirrored ?? 0}
      {missing > 0 && <span className="ml-2 text-xs text-amber-700">{missing} missing</span>}
    </span>
  )
}

export function ExternalCatalogPage() {
  const navigate = useNavigate()
  const { data: providerData, isLoading: providersLoading } = useProviders()
  const providers = providerData?.providers ?? []
  const [selected, setSelected] = useState<string | null>(null)
  const provider = providers.find((p) => p.key === selected) ?? providers[0]
  const providerKey = provider?.key ?? ''

  const [q, setQ] = useState('')
  const [view, setView] = useState<View>('all')
  const [offset, setOffset] = useState(0)

  const { data, isLoading } = useExternalArtists(providerKey, {
    ...VIEWS.find((v) => v.value === view)!.params,
    q: q || undefined,
    limit: PAGE_SIZE,
    offset,
  })
  const sync = useSyncArtists(providerKey)

  const stats = provider?.stats
  const cards = [
    { label: 'Artists in catalog', value: stats?.artists, icon: Users },
    { label: 'Linked to our artists', value: stats?.linked, icon: Link2 },
    { label: 'Missing artworks (linked artists)', value: stats?.artworks.missingForLinked, icon: ImageOff },
    { label: 'Shortlisted new artists', value: stats?.shortlisted, icon: Star },
  ]

  const open = (externalId: string) => navigate(`/app/external/${providerKey}/artists/${externalId}`)
  const pagination = data?.pagination

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">External catalogs</h1>
          <p className="text-sm text-slate-500">
            Third-party artist and artwork databases: find the artists we already represent, see
            which of their works we are missing, and curate the ones we do not have.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {providers.length > 1 &&
            providers.map((p) => (
              <Button
                key={p.key}
                size="sm"
                variant={p.key === providerKey ? 'default' : 'outline'}
                onClick={() => {
                  setSelected(p.key)
                  setOffset(0)
                }}
              >
                {p.label}
              </Button>
            ))}
          <Button
            onClick={() => sync.mutate()}
            disabled={!provider?.configured || sync.isPending}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${sync.isPending ? 'animate-spin' : ''}`} />
            {sync.isPending ? 'Syncing…' : `Sync ${provider?.label ?? ''} artists`}
          </Button>
        </div>
      </div>

      {provider && !provider.configured && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm text-amber-900">
              <KeyRound className="h-4 w-4" />
              {provider.label} is not connected
            </CardTitle>
            <CardDescription className="text-amber-800">
              This server has no API key for {provider.label} ({provider.configHint}). Anything
              already mirrored is still shown below; syncing and live search are unavailable until
              the key is set.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map(({ label, value, icon: Icon }) => (
          <Card key={label}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">{label}</CardTitle>
              <Icon className="h-4 w-4 text-slate-400" />
            </CardHeader>
            <CardContent>
              {providersLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <div className="text-2xl font-semibold tabular-nums">
                  {value?.toLocaleString() ?? '—'}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="artists">
        <TabsList>
          <TabsTrigger value="artists">Artists</TabsTrigger>
          <TabsTrigger value="search">Search artworks</TabsTrigger>
        </TabsList>

        <TabsContent value="artists">
          <Card>
            <CardHeader className="space-y-3">
              <Input
                value={q}
                onChange={(e) => {
                  setQ(e.target.value)
                  setOffset(0)
                }}
                placeholder="Search name, handle or catalog id…"
                className="max-w-xs"
              />
              <div className="flex flex-wrap gap-1">
                {VIEWS.map((v) => (
                  <Button
                    key={v.value}
                    variant={view === v.value ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => {
                      setView(v.value)
                      setOffset(0)
                    }}
                  >
                    {v.label}
                    {v.value === 'suggested' && stats?.suggested ? ` (${stats.suggested})` : ''}
                  </Button>
                ))}
              </div>
            </CardHeader>
            <CardContent>
              {isLoading || providersLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : !data?.artists.length ? (
                <p className="py-12 text-center text-sm text-slate-500">
                  {stats?.artists
                    ? 'No artists match these filters.'
                    : `Nothing mirrored yet. Sync ${provider?.label ?? 'the catalog'} to pull its artist roster.`}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Artist</TableHead>
                        <TableHead className="text-right">Artworks in catalog</TableHead>
                        <TableHead>In ARTHAUS</TableHead>
                        <TableHead>We hold</TableHead>
                        <TableHead>Review</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.artists.map((a) => (
                        <TableRow
                          key={a.externalId}
                          className="cursor-pointer hover:bg-slate-50"
                          onClick={() => open(a.externalId)}
                        >
                          <TableCell>
                            <div className="font-medium">{a.name}</div>
                            <div className="text-xs text-slate-500">
                              {[a.username ? `@${a.username}` : null, a.country].filter(Boolean).join(' · ') ||
                                `#${a.externalId}`}
                            </div>
                          </TableCell>
                          <TableCell className="text-right text-sm tabular-nums">
                            {a.artworkCount?.toLocaleString() ?? '—'}
                          </TableCell>
                          <TableCell>
                            <MatchBadge artist={a} />
                          </TableCell>
                          <TableCell>
                            <CoverageCell artist={a} />
                          </TableCell>
                          <TableCell>
                            {a.matchStatus === 'linked' ? (
                              <span className="text-slate-400">—</span>
                            ) : (
                              <ReviewBadge status={a.reviewStatus} />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              {pagination && pagination.total > 0 && (
                <div className="flex items-center justify-between pt-3">
                  <p className="text-xs text-slate-500">
                    {offset + 1}–{offset + (data?.artists.length ?? 0)} of{' '}
                    {pagination.total.toLocaleString()}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={offset === 0}
                      onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                    >
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!pagination.hasMore}
                      onClick={() => setOffset(offset + PAGE_SIZE)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="search">
          <Card>
            <CardContent className="pt-6">
              {provider?.configured ? (
                <CatalogSearch provider={providerKey} onOpenArtist={open} />
              ) : (
                <p className="py-10 text-center text-sm text-slate-500">
                  Live search needs the {provider?.label ?? 'catalog'} API key.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
